import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import enCommon from "@/locales/en/common.json";
import arCommon from "@/locales/ar/common.json";
import { financeMessages } from "@/features/finance/i18n";

export const SUPPORTED_LOCALES = ["en", "ar"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const RTL_LOCALES: ReadonlySet<string> = new Set(["ar"]);

/**
 * The name to show a reader working in `locale`.
 *
 * A record may carry a second name in Arabic. A reader working in Arabic gets
 * it when it exists and the Latin name when it does not — falling back to the
 * name that exists beats showing a blank where a name belongs. A reader
 * working in any other language always gets the Latin name, which is the one
 * the record is required to have.
 */
export function localizedName(
  latin: string,
  arabic: string | null | undefined,
  locale: string,
): string {
  if (!locale.startsWith("ar")) return latin;
  const trimmed = arabic?.trim();
  return trimmed === undefined || trimmed === "" ? latin : trimmed;
}

export type MessageTree = { [key: string]: string | MessageTree };
type Tree = MessageTree;

/**
 * Merge a lane's bundle into the shared catalogue.
 *
 * It has to recurse. A lane keeps its strings under the same top-level key the
 * shared file uses — `finance.*` in both — so a shallow spread would not merge
 * the two, it would replace one wholesale and take every sibling with it. That
 * loses strings silently: the key resolves to its English default and the
 * screen reads correctly in English while reading English in Arabic too.
 *
 * The lane wins on a genuine collision, since its strings sit beside the
 * screens that use them.
 */
export function mergeMessages(base: Tree, lane: Tree): Tree {
  const merged: Tree = { ...base };
  for (const [key, value] of Object.entries(lane)) {
    const existing = merged[key];
    merged[key] =
      typeof value === "object" && typeof existing === "object"
        ? mergeMessages(existing, value)
        : value;
  }
  return merged;
}

export function isRtl(locale: string): boolean {
  return RTL_LOCALES.has(locale.split("-")[0] ?? locale);
}

/** Apply lang + dir to <html>. Every locale change must go through setLocale(). */
export function applyDocumentDirection(locale: string): void {
  const root = document.documentElement;
  root.lang = locale;
  root.dir = isRtl(locale) ? "rtl" : "ltr";
}

export async function setLocale(locale: Locale): Promise<void> {
  await i18n.changeLanguage(locale);
  applyDocumentDirection(locale);
}

export function initI18n(): typeof i18n {
  if (i18n.isInitialized) return i18n;
  // Register BEFORE init so the initial detected language also applies dir.
  i18n.on("languageChanged", (lng) => {
    if (typeof document !== "undefined") applyDocumentDirection(lng);
  });
  void i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      // A feature lane may keep its own strings beside its screens. They are
      // merged here rather than only in that lane's test harness — a bundle
      // the tests load and the app does not is a screen that reads correctly
      // in a test and in English everywhere else.
      resources: {
        en: { common: mergeMessages(enCommon as Tree, financeMessages.en as Tree) },
        ar: { common: mergeMessages(arCommon as Tree, financeMessages.ar as Tree) },
      },
      fallbackLng: "en",
      supportedLngs: [...SUPPORTED_LOCALES],
      defaultNS: "common",
      ns: ["common"],
      interpolation: { escapeValue: false },
      detection: {
        order: ["localStorage", "navigator"],
        caches: ["localStorage"],
        lookupLocalStorage: "erp.locale",
      },
      returnNull: false,
    })
    .then(() => {
      // languageChanged may fire before the DOM listener sees it on some
      // detector paths — apply once more with the resolved language.
      if (typeof document !== "undefined") applyDocumentDirection(i18n.language);
    });
  return i18n;
}

export default i18n;
